import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes, css } from 'styled-components';
import { ChevronLeft, Volume2, RotateCcw, Swords } from 'lucide-react';
import { flashcardActions } from '../../redux/actions/flashcard.actions';
import { flashcardService } from '../../services/flashcard.service';
import { leaderboardService } from '../../services/leaderboard.service';
import { apiClient } from '../../helpers';
import { ChallengeModal } from './ChallengeModal';
import ChallengeResultModal from './ChallengeResultModal';
import ChallengeSentModal from './ChallengeSentModal';
import LeaderboardModal from './LeaderboardModal';

// ============= ANIMATIONS =============
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const correctFlash = keyframes`
  0%, 100% { background-color: #d4edda; }
  50% { background-color: #fff; }
`;

const errorFlash = keyframes`
  0%, 100% { background-color: #f8d7da; }
  50% { background-color: #fff; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const confetti = keyframes`
  0% {
    opacity: 1;
    transform: translate(0, 0) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: translate(var(--tx), var(--ty)) rotate(360deg);
  }
`;

const emojiPulse = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
    filter: drop-shadow(0 0 0px rgba(255, 215, 0, 0.5));
  }
  50% {
    transform: scale(1.15) rotate(5deg);
    filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.8));
  }
`;

const emojiFloat = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
`;

const emojiBounce = keyframes`
  0%, 100% {
    transform: scale(1) translateY(0);
  }
  50% {
    transform: scale(1.1) translateY(-5px);
  }
`;

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// ============= STYLED COMPONENTS =============
const Container = styled.div`
  min-height: 100vh;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const QuizWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 40px;
  animation: ${slideIn} 0.5s ease-out;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
    max-height: 95vh;
    overflow-y: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  color: #667eea;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    color: #764ba2;
    transform: translateX(-5px);
  }
`;

const SetTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin: 0;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const ProgressContainer = styled.div`
  text-align: right;
  font-size: 16px;
  color: #667eea;
  font-weight: 600;
  display: flex;
  gap: 20px;
  align-items: center;
`;

const TimerDisplay = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #667eea;
  font-family: 'Courier New', monospace;
  min-width: 80px;
`;

const ScoreDisplay = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #28a745;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const QuestionNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 20px;
  text-align: center;
`;

const QuestionText = styled.div<{ $isCorrect?: boolean | null; $answered?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin: 40px 0;
  padding: 50px;
  background: white;
  border: 3px solid #ddd;
  border-radius: 15px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  word-wrap: break-word;
  animation: ${slideUp} 0.4s ease-out;
  ${props => props.$answered && props.$isCorrect && css`animation: ${correctFlash} 0.6s ease-in-out;`}
  ${props => props.$answered && !props.$isCorrect && css`animation: ${errorFlash} 0.6s ease-in-out;`}

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 16px 0;
    padding: 20px;
    min-height: 100px;
    border-width: 2px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    padding: 16px;
    margin: 12px 0;
    min-height: 80px;
  }
`;

const AudioButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 15px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #764ba2;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 40px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    margin: 16px 0;
  }

  @media (max-width: 480px) {
    gap: 8px;
    margin: 12px 0;
  }
`;

interface OptionButtonProps {
  $isSelected?: boolean;
  $isCorrect?: boolean;
  $isWrong?: boolean;
  $disabled?: boolean;
  $isFlashing?: boolean;
}

const OptionPrefix = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 700;
  color: #667eea;
  
  @media (max-width: 768px) {
    left: 12px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    left: 10px;
    font-size: 12px;
  }
`;

const OptionText = styled.span`
  flex: 1;
  text-align: center;
  padding: 0 30px;
  
  @media (max-width: 768px) {
    padding: 0 25px;
  }
  
  @media (max-width: 480px) {
    padding: 0 20px;
  }
`;

const OptionButton = styled.button<OptionButtonProps>`
  position: relative;
  padding: 25px;
  font-size: 20px;
  border: 3px solid #ddd;
  border-radius: 15px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  color: #333;
  animation: ${slideUp} 0.4s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  &:hover:not(:disabled) {
    border-color: #667eea;
    background: #f8f9ff;
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.2);
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  ${props => props.$isFlashing && css`animation: ${correctFlash} 0.6s ease-in-out;`}
  
  ${props => props.$isCorrect && css`
    border-color: #28a745;
    background: #d4edda;
    color: #155724;
    animation: ${correctFlash} 0.6s ease-in-out;
  `}
  
  ${props => props.$isWrong && css`
    border-color: #dc3545;
    background: #f8d7da;
    color: #721c24;
    animation: ${errorFlash} 0.6s ease-in-out;
  `}

  @media (max-width: 768px) {
    padding: 16px 12px;
    font-size: 14px;
    border-width: 2px;
    border-radius: 10px;
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.3;
  }

  @media (max-width: 480px) {
    padding: 12px 8px;
    font-size: 12px;
    min-height: 50px;
    border-radius: 8px;
  }
`;

const FeedbackMessage = styled.div<{ type: 'correct' | 'incorrect' }>`
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  margin: 20px 0;
  animation: ${slideUp} 0.4s ease-out;
  
  ${props => props.type === 'correct' ? css`
    color: #28a745;
  ` : css`
    color: #dc3545;
  `}

  @media (max-width: 768px) {
    font-size: 16px;
    margin: 12px 0;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ResultsContainer = styled.div`
  text-align: center;
  animation: ${slideIn} 0.5s ease-out;
  position: relative;
  overflow: hidden;
`;

const ConfettiContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 9999;
`;

const ConfettiPiece = styled.div.attrs<{ $delay: number; $duration: number }>(props => ({
  style: {
    left: Math.random() * 100 + '%',
    top: '-10px',
  },
})) <{ $delay: number; $duration: number }>`
  position: fixed;
  width: 8px;
  height: 8px;
  pointer-events: none;
  animation: ${confetti} ${props => props.$duration}ms linear ${props => props.$delay}ms forwards;
  --tx: ${Math.random() * 400 - 200}px;
  --ty: ${Math.random() * 400 + 100}px;
`;

const ResultsTitle = styled.h2`
  font-size: 36px;
  color: #333;
  margin-bottom: 12px;
  letter-spacing: 1px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 6px;
  white-space: nowrap;
`;

const ResultsTitleText = styled.span`
  font-size: 24px;
  font-weight: bold;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  white-space: nowrap;
`;

const CelebrationEmoji = styled.span<{ $delay: number; $order: number }>`
  display: inline-block;
  font-size: 32px;
  animation: ${emojiBounce} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
  animation-delay: ${props => props.$delay}s;
  filter: drop-shadow(0 2px 4px rgba(102, 126, 234, 0.2));
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: inherit;
  -webkit-text-fill-color: unset;
  text-fill-color: unset;
  background: none;
  background-clip: unset;
  -webkit-background-clip: unset;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ResultsScoreDisplay = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: #667eea;
  margin: 30px 0;
  text-shadow: 0 2px 10px rgba(102, 126, 234, 0.2);
`;

const StatisticsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    margin: 20px 0;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin: 15px 0;
  }
`;

const StatisticCard = styled.div<{ color: string; $isBigCard?: boolean }>`
  padding: 20px;
  background: ${props => props.color}20;
  border-left: 4px solid ${props => props.color};
  border-radius: 10px;
  animation: ${popIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  div:first-child {
    font-size: 28px;
    font-weight: bold;
    color: ${props => props.color};
  }
  
  div:last-child {
    font-size: 14px;
    color: #666;
    margin-top: 8px;
  }
  
  .stat-fraction {
    font-size: 12px;
    color: #999;
    display: inline;
  }
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 14px;
    
    div:first-child {
      font-size: 22px;
    }
    
    div:last-child {
      font-size: 12px;
      margin-top: 6px;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 40px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 15px 40px;
  font-size: 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(Button)`
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  
  &:hover {
    background: #f8f9ff;
  }
`;

// ============= TYPES =============
interface Question {
  id: string;
  front: string;
  back: string;
  audio_url?: string;
  type: 'en-to-vi' | 'vi-to-en';
}

interface Option {
  text: string;
  isCorrect: boolean;
}

interface QuizState {
  currentQuestionIndex: number;
  score: number;
  answered: boolean;
  selectedOption: string | null;
  isCorrect: boolean | null;
  answers: Array<{
    questionId: string;
    selectedAnswer: string;
    isCorrect: boolean;
  }>;
}

// ============= MAIN COMPONENT =============
const FlashcardQuizPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const params = new URLSearchParams(window.location.search);
  const from = params.get('from');
  const folderId = params.get('folderId');
  const baseUrl = location.pathname.startsWith('/student') ? '/student/flashcards' : '/flashcards';

  // Get challenge state from location if passed
  const challengeState = (location.state as any)?.isChallenge ? {
    challengeId: (location.state as any)?.challengeId,
    isChallenge: true,
    gameType: (location.state as any)?.gameType,
    isAccepter: (location.state as any)?.isAccepter || false, // true if this player is accepting a challenge
  } : null;

  const { cards = [], sets = [] } = useSelector((state: any) => state.flashcard);
  const authState = useSelector((state: any) => state.authentication);
  const user = authState?.userInfo;

  // Local state for set & cards — loaded directly via API so cross-class sets work
  const [localSet, setLocalSet] = useState<any>(null);
  const [localCards, setLocalCards] = useState<any[]>([]);

  // DEBUG: Log Redux state structure
  useEffect(() => {
    console.log('🔍 FlashcardQuizPage Redux State:');
    console.log('authState:', authState);
    console.log('user (userInfo):', user);
    console.log('user?.id:', user?.id);
    console.log('user?.studentId:', user?.studentId);
    console.log('user?.email:', user?.email);
    console.log('user?.role:', user?.role);
  }, [authState, user]);
  const currentSet = localSet;
  const setCards = useMemo(() => localCards, [localCards]);

  const [quizState, setQuizState] = useState<QuizState>({
    currentQuestionIndex: 0,
    score: 0,
    answered: false,
    selectedOption: null,
    isCorrect: null,
    answers: [],
  });

  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentOptions, setCurrentOptions] = useState<Option[]>([]);
  const [quizComplete, setQuizComplete] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [elapsedMillis, setElapsedMillis] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const backgroundMusicRef = React.useRef<AudioContext | null>(null);

  // Admin (role=0) or Teacher (role=1) keep ranking in SetDetail; others see it in result screen
  const isAdminOrTeacher = user?.role === 0 || user?.role === 1;

  // Leaderboard modal state (for non-admin/teacher users in result screen)
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  // Challenge modal state
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [currentStudentId, setCurrentStudentId] = useState<number | null>(null);
  const [currentStudentGifts, setCurrentStudentGifts] = useState(0);

  // Challenge result modal state
  const [showResultModal, setShowResultModal] = useState(false);
  const [challengeResult, setChallengeResult] = useState<{
    challengerName: string;
    challengerAvatar?: string;
    challengerScore: number;
    challengerTime?: number;
    accepterName: string;
    accepterAvatar?: string;
    accepterScore: number;
    accepterTime?: number;
    winner: 'challenger' | 'accepter' | 'tie';
    giftAmount: number;
    isCurrentUserWinner: boolean;
    gameType: 'quiz' | 'matching';
  } | null>(null);

  // Challenge sent modal state
  const [showSentModal, setShowSentModal] = useState(false);
  const [sentAccepterName, setSentAccepterName] = useState('');

  // Load student info for challenge
  useEffect(() => {
    const loadStudentInfo = async () => {
      try {
        let studentId = user?.studentId;

        // Try to get from JWT if not available
        if (!studentId) {
          const accessToken = localStorage.getItem('accessToken');
          if (accessToken) {
            try {
              const parts = accessToken.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                if (payload.studentId) {
                  studentId = payload.studentId;
                }
              }
            } catch (e) {
              console.log('Failed to decode JWT');
            }
          }
        }

        if (studentId) {
          setCurrentStudentId(studentId);
          try {
            const res = await apiClient.get(`/api/students/${studentId}`);
            if (res.data) {
              setCurrentStudentGifts(res.data.gift || 0);
            }
          } catch (err) {
            console.log('Could not fetch student gifts');
          }
        }
      } catch (error) {
        console.error('Error loading student info:', error);
      }
    };

    loadStudentInfo();
  }, [user?.studentId]);

  // Load set and cards directly via API (supports cross-class sets for challenges)
  useEffect(() => {
    const loadData = async () => {
      try {
        const [setData, cardsData] = await Promise.all([
          flashcardService.getSetById(Number(id)),
          flashcardService.getAllCards(Number(id)),
        ]);
        setLocalSet(setData);
        setLocalCards(Array.isArray(cardsData) ? cardsData : []);
      } catch (err) {
        console.error('[FlashcardQuizPage] Failed to load set/cards:', err);
      }
    };
    loadData();
  }, [id]);

  // Timer effect
  useEffect(() => {
    if (startTime === null || quizComplete) return;

    const timer = setInterval(() => {
      setElapsedMillis(Date.now() - startTime);
    }, 10);

    return () => clearInterval(timer);
  }, [startTime, quizComplete]);

  // Start timer when questions load
  useEffect(() => {
    if (questions.length > 0 && startTime === null && !quizComplete) {
      setStartTime(Date.now());
      playBackgroundMusic(); // Start background music
    }
  }, [questions, startTime, quizComplete]);

  // Cleanup music when component unmounts
  useEffect(() => {
    return () => {
      stopBackgroundMusic();
    };
  }, []);

  // Stop music once the quiz ends
  useEffect(() => {
    console.log(`\n========== LEADERBOARD SAVE CHECK ==========`);
    console.log(`quizComplete: ${quizComplete}`);

    if (quizComplete) {
      console.log('✅ Quiz is complete, proceeding to save...');
      stopBackgroundMusic();
      // Save to leaderboard if user is a student
      console.log('Quiz complete - attempting to save leaderboard');
      console.log('User object:', user);
      console.log('User id:', user?.id);
      console.log('Set ID:', id);
      console.log('Quiz score:', quizState.score);
      console.log('Elapsed millis:', elapsedMillis);

      if (user?.studentId && Number(id)) {
        console.log('✅ All conditions passed! Calling leaderboardService.saveGameCompletion...');
        console.log(`Parameters: studentId=${user.studentId}, setId=${Number(id)}, gameType=quiz, time=${elapsedMillis}, score=${quizState.score}`);
        leaderboardService.saveGameCompletion(
          Number(user.studentId),
          Number(id),
          'quiz',
          elapsedMillis,
          quizState.score // Include number of correct answers for quiz
        ).then(() => {
          console.log('✅ Leaderboard saved successfully');
        }).catch(err => {
          console.error('❌ Failed to save leaderboard:', err);
        });
      } else {
        console.log('❌ Cannot save - missing conditions:');
        console.log(`  - user?.studentId exists: ${!!user?.studentId}`);
        console.log(`  - Number(id) is valid: ${!!Number(id)}`);
      }

      // Auto-submit score if this is a challenge and user is the accepter
      if (challengeState?.isChallenge && challengeState?.isAccepter && challengeState?.challengeId) {
        console.log('🎯 Auto-submitting accepter score for challenge...');
        apiClient.patch(`/api/challenges/${challengeState.challengeId}/submit-accepter-score`, {
          score: quizState.score,
          time: elapsedMillis, // Include time for tiebreaker
        }).then((response) => {
          console.log('✅ Challenge score submitted successfully');
          // Show result modal with challenge details
          const challenge = response.data;
          const isAccepterWinner = challenge.winner === 'accepter';
          setChallengeResult({
            challengerName: challenge.challenger?.name || 'Người thách đấu',
            challengerAvatar: challenge.challenger?.avatar,
            challengerScore: challenge.challengerScore,
            challengerTime: challenge.challengerTime,
            accepterName: challenge.accepter?.name || 'Bạn',
            accepterAvatar: challenge.accepter?.avatar,
            accepterScore: challenge.accepterScore,
            accepterTime: challenge.accepterTime,
            winner: challenge.winner,
            giftAmount: challenge.giftAmount,
            isCurrentUserWinner: isAccepterWinner,
            gameType: 'quiz',
          });
          setShowResultModal(true);
        }).catch(err => {
          console.error('❌ Failed to submit challenge score:', err);
        });
      }
    } else {
      console.log('⏳ Quiz not complete yet, skipping save');
    }
    console.log(`==========================================\n`);
  }, [quizComplete]); // Only depend on quizComplete!

  // Generate quiz questions
  useEffect(() => {
    if (setCards.length === 0) return;

    const generatedQuestions: Question[] = setCards.map((card: any) => ({
      id: card.id.toString(),
      front: card.front,
      back: card.back,
      audio_url: card.audio_url,
      type: Math.random() > 0.5 ? 'en-to-vi' : 'vi-to-en',
    }));

    setQuestions(generatedQuestions);
  }, [setCards]);

  // Generate options for current question
  useEffect(() => {
    if (questions.length === 0 || quizComplete) return;

    const currentQuestion = questions[quizState.currentQuestionIndex];
    if (!currentQuestion) return;

    // Get correct answer
    const correctAnswer = currentQuestion.type === 'en-to-vi'
      ? currentQuestion.back
      : currentQuestion.front;

    // Get 3 random wrong answers from other cards
    const otherCards = setCards.filter((c: any) => c.id !== Number(currentQuestion.id));
    const wrongAnswers: string[] = [];

    while (wrongAnswers.length < 3 && otherCards.length > 0) {
      const randomIndex = Math.floor(Math.random() * otherCards.length);
      const wrongCard = otherCards[randomIndex];
      const wrongAnswer = currentQuestion.type === 'en-to-vi'
        ? wrongCard.back
        : wrongCard.front;

      if (!wrongAnswers.includes(wrongAnswer)) {
        wrongAnswers.push(wrongAnswer);
      }

      otherCards.splice(randomIndex, 1);
    }

    // Combine and shuffle
    const allOptions = [
      { text: correctAnswer, isCorrect: true },
      ...wrongAnswers.map(w => ({ text: w, isCorrect: false })),
    ];

    // Fisher-Yates shuffle
    for (let i = allOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
    }

    setCurrentOptions(allOptions);
    setQuizState(prev => ({
      ...prev,
      answered: false,
      selectedOption: null,
      isCorrect: null,
    }));
  }, [quizState.currentQuestionIndex, questions, setCards, quizComplete]);

  // Play celebration sound when quiz completes
  useEffect(() => {
    if (!quizComplete) return;

    const playSound = async () => {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const now = audioContext.currentTime;

        // Create cheerful celebration tones
        const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
        notes.forEach((freq) => {
          const osc = audioContext.createOscillator();
          const gain = audioContext.createGain();
          osc.connect(gain);
          gain.connect(audioContext.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.3, now);
          gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          osc.start(now);
          osc.stop(now + 0.3);
        });
      } catch (e) {
        // Audio context not available
      }
    };
    playSound();
  }, [quizComplete]);

  const playAudio = (audioUrl?: string) => {
    if (!soundEnabled) return;

    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch(err => console.error('Error playing audio:', err));
    }
  };

  const playFeedbackSound = (isCorrect: boolean) => {
    if (!soundEnabled) return;

    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    if (isCorrect) {
      // Happy sound - ascending notes
      const notes = [523, 659, 784]; // C, E, G (ascending)
      let time = audioContext.currentTime;

      notes.forEach((freq, idx) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();

        osc.connect(gain);
        gain.connect(audioContext.destination);

        osc.frequency.value = freq;
        osc.type = 'sine';

        gain.gain.setValueAtTime(0.3, time);
        gain.gain.exponentialRampToValueAtTime(0.01, time + 0.15);

        osc.start(time);
        osc.stop(time + 0.15);

        time += 0.1;
      });
    } else {
      // Sad sound - descending notes
      const notes = [523, 440, 330]; // C, A, E (descending)
      let time = audioContext.currentTime;

      notes.forEach((freq) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();

        osc.connect(gain);
        gain.connect(audioContext.destination);

        osc.frequency.value = freq;
        osc.type = 'sine';

        gain.gain.setValueAtTime(0.3, time);
        gain.gain.exponentialRampToValueAtTime(0.01, time + 0.2);

        osc.start(time);
        osc.stop(time + 0.2);

        time += 0.12;
      });
    }
  };

  // Play background music
  const playBackgroundMusic = () => {
    if (!soundEnabled) return;

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      backgroundMusicRef.current = audioContext;

      // Simple pleasant loop: C-D-E-F (ascending scale, repeating)
      const notes = [262, 294, 330, 349]; // C, D, E, F
      let currentTime = audioContext.currentTime;
      const duration = 0.3; // Each note duration
      const totalLoopTime = notes.length * duration;

      // Create a continuous loop that plays for the entire quiz
      const createLoop = (startTime: number, endTime: number) => {
        let time = startTime;
        while (time < endTime) {
          notes.forEach((freq) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();

            osc.connect(gain);
            gain.connect(audioContext.destination);

            osc.frequency.value = freq;
            osc.type = 'sine';

            // Soft volume for background
            gain.gain.setValueAtTime(0.05, time);
            gain.gain.exponentialRampToValueAtTime(0.01, time + duration);

            osc.start(time);
            osc.stop(time + duration);

            time += duration;
          });
        }
      };

      // Play for 10 minutes (600 seconds) - should be enough for quiz
      createLoop(audioContext.currentTime, audioContext.currentTime + 600);
    } catch (err) {
      console.log('Background music not available');
    }
  };

  // Stop background music
  const stopBackgroundMusic = () => {
    const ctx = backgroundMusicRef.current;
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => { });
    }
    backgroundMusicRef.current = null;
  };

  const handleOptionSelect = (option: Option) => {
    if (quizState.answered) return;

    const isCorrect = option.isCorrect;

    // Play feedback sound
    playFeedbackSound(isCorrect);

    const currentIndex = quizState.currentQuestionIndex;
    const totalQuestions = questions.length;
    const newScore = isCorrect ? quizState.score + 1 : quizState.score;

    const newAnswers = [
      ...quizState.answers,
      {
        questionId: questions[currentIndex].id,
        selectedAnswer: option.text,
        isCorrect,
      },
    ];

    console.log(`[Quiz] Current question: ${currentIndex + 1}/${totalQuestions}`);

    setQuizState(prev => ({
      ...prev,
      answered: true,
      selectedOption: option.text,
      isCorrect,
      score: newScore,
      answers: newAnswers,
    }));

    // Auto-advance to next question after 350ms (faster for better UX)
    setTimeout(() => {
      console.log(`[Quiz] Timeout - Current index: ${currentIndex}, Total: ${totalQuestions}`);
      if (currentIndex + 1 >= totalQuestions) {
        console.log('🎯 QUIZ COMPLETE! Setting quizComplete to TRUE');
        setQuizComplete(true);
      } else {
        console.log(`📝 Moving to next question: ${currentIndex + 2}/${totalQuestions}`);
        setQuizState(prev => ({
          ...prev,
          currentQuestionIndex: prev.currentQuestionIndex + 1,
        }));
      }
    }, 350);
  };

  const handleRestart = () => {
    setQuizState({
      currentQuestionIndex: 0,
      score: 0,
      answered: false,
      selectedOption: null,
      isCorrect: null,
      answers: [],
    });
    setStartTime(null);
    setElapsedMillis(0);
    setQuizComplete(false);
  };

  const handleSubmitChallengeScore = async () => {
    if (!challengeState?.challengeId || !user?.id) {
      alert('Missing challenge information');
      return;
    }

    try {
      await apiClient.patch(`/api/challenges/${challengeState.challengeId}/submit-challenger-score`, {
        score: quizState.score,
      });

      alert('🎯 Kết quả của bạn đã được gửi! Đợi đối thủ hoàn thành để so kết quả.');
      navigate('/student');
    } catch (error) {
      console.error('Failed to submit challenge score:', error);
      alert('Không thể gửi kết quả thách đấu');
    }
  };

  const handleChallengeSubmit = async (accepterId: number, accepterName: string, giftAmount: number, gameType: 'quiz' | 'matching', challengerScore?: number) => {
    if (!currentStudentId) {
      alert('Vui lòng đăng nhập để thách đấu');
      return;
    }

    try {
      await apiClient.post('/api/challenges', {
        challengerId: currentStudentId,
        accepterId,
        setId: Number(id),
        gameType,
        giftAmount,
        challengerScore, // Include score when creating challenge
        challengerTime: elapsedMillis, // Include time when creating challenge
      });

      setCurrentStudentGifts(Math.max(0, currentStudentGifts - giftAmount));
      setSentAccepterName(accepterName);
      setShowSentModal(true);
    } catch (error: any) {
      alert(`Lỗi: ${error?.response?.data?.message || error.message || 'Không thể gửi thách đấu'}`);
    }
  };

  const handleSentModalClose = () => {
    setShowSentModal(false);
    navigate('/student');
  };

  const handleBack = () => {
    stopBackgroundMusic();
    let url = `${baseUrl}/sets/${id}/detail`;
    if (from && folderId) {
      url += `?from=${from}&folderId=${folderId}`;
    }
    navigate(url);
  };

  // Loading state
  if (!currentSet || questions.length === 0) {
    return (
      <Container>
        <QuizWrapper>
          <BackButton onClick={handleBack}>
            <ChevronLeft size={24} />
            Quay lại
          </BackButton>
          <p style={{ textAlign: 'center' }}>Đang tải câu hỏi...</p>
        </QuizWrapper>
      </Container>
    );
  }

  // Format elapsed time
  const formatTime = (millis: number) => {
    const totalSeconds = Math.floor(millis / 1000);
    const ms = Math.floor((millis % 1000) / 10);
    return `${totalSeconds.toString().padStart(2, '0')}:${ms.toString().padStart(2, '0')}`;
  };

  // Results screen
  if (quizComplete) {
    const percentage = Math.round((quizState.score / questions.length) * 100);
    const correct = quizState.score;
    const incorrect = questions.length - quizState.score;

    // Get celebration emoji based on score
    const getCelebration = (pct: number): string[] => {
      if (pct === 100) return ['🌟', '✨', '🎊'];
      if (pct >= 80) return ['🎉', '🎈', '🎁'];
      if (pct >= 60) return ['👏', '💪', '🌈'];
      if (pct >= 40) return ['💪', '🏆', '⭐'];
      return ['💯', '📚', '✍️'];
    };

    // Generate confetti pieces
    const generateConfetti = () => {
      const pieces = [];
      const colors = ['#FFD700', '#FF69B4', '#1E90FF', '#32CD32', '#FF6347', '#FFB6C1'];
      for (let i = 0; i < 40; i++) {
        pieces.push(
          <ConfettiPiece
            key={i}
            style={{
              left: Math.random() * 100 + '%',
              top: -10 + 'px',
              backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            }}
            $delay={Math.random() * 200}
            $duration={2000 + Math.random() * 1000}
          />
        );
      }
      return pieces;
    };

    return (
      <Container>
        <ConfettiContainer>
          {generateConfetti()}
        </ConfettiContainer>
        <QuizWrapper>
          <ResultsContainer>
            <ResultsTitle>
              {getCelebration(percentage).map((emoji, idx) => (
                <CelebrationEmoji key={idx} $delay={idx * 0.1} $order={idx + 1}>{emoji}</CelebrationEmoji>
              ))}
              <ResultsTitleText>Chúc Mừng!</ResultsTitleText>
              {getCelebration(percentage).map((emoji, idx) => (
                <CelebrationEmoji key={`end-${idx}`} $delay={(2 - idx) * 0.1} $order={5 + idx}>{emoji}</CelebrationEmoji>
              ))}
            </ResultsTitle>

            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea', marginBottom: '15px', marginTop: '20px' }}>
              {percentage}%
            </div>

            <div style={{ fontSize: '16px', color: '#667eea', marginBottom: '30px' }}>
              ⏱️ Thời gian: {formatTime(elapsedMillis)}
            </div>

            <StatisticsContainer>
              <StatisticCard color="#28a745">
                <div>✓ {correct}<span className="stat-fraction">/{questions.length}</span></div>
                <div>Câu Đúng</div>
              </StatisticCard>
              <StatisticCard color="#dc3545">
                <div>✗ {incorrect}<span className="stat-fraction">/{questions.length}</span></div>
                <div>Câu Sai</div>
              </StatisticCard>
            </StatisticsContainer>

            <ButtonGroup>
              <Button onClick={handleRestart}>
                <RotateCcw size={20} style={{ marginRight: '8px' }} />
                Làm Lại
              </Button>
              {challengeState ? (
                <SecondaryButton onClick={handleSubmitChallengeScore}>
                  🎯 Gửi Kết Quả Thách Đấu
                </SecondaryButton>
              ) : (
                <>
                  {!isAdminOrTeacher && (
                    <SecondaryButton onClick={() => setShowLeaderboard(true)} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', border: 'none' }}>
                      🏆 Xếp Hạng
                    </SecondaryButton>
                  )}
                  {currentStudentId && (
                    <SecondaryButton onClick={() => setShowChallengeModal(true)} style={{ background: 'linear-gradient(90deg, #ff6b6b 0%, #ee5a6f 100%)' }}>
                      <Swords size={20} style={{ marginRight: '8px' }} />
                      Thách Đấu
                    </SecondaryButton>
                  )}
                  <SecondaryButton onClick={handleBack}>
                    Quay Lại Chi Tiết Tập Từ
                  </SecondaryButton>
                </>
              )}
            </ButtonGroup>
          </ResultsContainer>
        </QuizWrapper>

        {/* Leaderboard Modal (for non-admin/teacher users) */}
        {!isAdminOrTeacher && (
          <LeaderboardModal
            setId={Number(id)}
            gameType="quiz"
            isOpen={showLeaderboard}
            onClose={() => setShowLeaderboard(false)}
            title="🏆 Xếp Hạng Trắc Nghiệm"
            totalCards={questions.length}
          />
        )}

        {/* Challenge Modal */}
        <ChallengeModal
          isOpen={showChallengeModal}
          onClose={() => setShowChallengeModal(false)}
          onSubmit={handleChallengeSubmit}
          gameType="quiz"
          currentStudentId={currentStudentId || 0}
          currentStudentGifts={currentStudentGifts}
          challengerScore={quizState.score}
        />

        {/* Challenge Result Modal */}
        <ChallengeResultModal
          isOpen={showResultModal}
          onClose={() => {
            setShowResultModal(false);
            navigate('/student');
          }}
          result={challengeResult}
        />

        {/* Challenge Sent Modal */}
        <ChallengeSentModal
          isOpen={showSentModal}
          onClose={handleSentModalClose}
          accepterName={sentAccepterName}
        />
      </Container>
    );
  }

  // Quiz screen
  const currentQuestion = questions[quizState.currentQuestionIndex];

  const questionText = currentQuestion.type === 'en-to-vi'
    ? currentQuestion.front
    : currentQuestion.back;

  return (
    <Container>
      <QuizWrapper>
        <Header>
          <BackButton onClick={handleBack}>
            <ChevronLeft size={24} />
            Quay lại
          </BackButton>
          <SetTitle>{currentSet.name}</SetTitle>
          <ProgressContainer>
            <TimerDisplay>⏱️ {formatTime(elapsedMillis)}</TimerDisplay>
            <ScoreDisplay>✓ {quizState.score}/{questions.length}</ScoreDisplay>
          </ProgressContainer>
        </Header>

        <QuestionNumber>
          Câu {quizState.currentQuestionIndex + 1}/{questions.length}
        </QuestionNumber>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', color: '#666', marginBottom: '15px' }}>
            {currentQuestion.type === 'en-to-vi'
              ? '💬 Nghĩa tiếng Việt của từ:'
              : '💬 Từ tiếng Anh của:'}
          </div>
        </div>

        <QuestionText $answered={quizState.answered} $isCorrect={quizState.isCorrect}>
          {questionText}
          {currentQuestion.audio_url && currentQuestion.type === 'en-to-vi' && (
            <AudioButton onClick={() => playAudio(currentQuestion.audio_url)}>
              <Volume2 size={24} />
            </AudioButton>
          )}
        </QuestionText>

        {quizState.answered && (
          <FeedbackMessage type={quizState.isCorrect ? 'correct' : 'incorrect'}>
            {quizState.isCorrect ? '✅ Chính xác!' : '❌ Sai rồi!'}
          </FeedbackMessage>
        )}

        <OptionsContainer>
          {currentOptions.map((option, idx) => (
            <OptionButton
              key={idx}
              onClick={() => handleOptionSelect(option)}
              disabled={quizState.answered}
              $isSelected={quizState.selectedOption === option.text}
              $isCorrect={quizState.answered && option.isCorrect}
              $isWrong={
                quizState.answered &&
                quizState.selectedOption === option.text &&
                !option.isCorrect
              }
            >
              <OptionPrefix>{String.fromCharCode(65 + idx)}.</OptionPrefix>
              <OptionText>{option.text}</OptionText>
            </OptionButton>
          ))}
        </OptionsContainer>
      </QuizWrapper>
    </Container>
  );
};

export default FlashcardQuizPage;
